import React, { useRef } from 'react';
import {
  connect,
  Provider,
  createStoreHook,
  createDispatchHook,
  createSelectorHook,
  EqualityFn,
} from 'react-redux';
import {
  createStore,
  applyMiddleware,
  combineReducers,
  bindActionCreators,
  ActionCreatorsMapObject,
} from 'redux';
import { UseSelectorOptions } from 'react-redux/es/hooks/useSelector';
import { fork, all } from 'redux-saga/effects';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';
import { reducers as streamReducers, sagas as streamSagas } from '../../Stream';
import { entitiesReducers } from '../../redux/entities';
import sharedConfigReducers from '@wix/da-redux-config/pkg/reducers';
import publicSessionReducers from '../../publicSession/reducers';
import modalsReducers from '../../Modals/redux/reducer';
import modalsSaga from '../../Modals/redux/saga';
import uploadsReducers from '../../redux/uploads/reducers';
import uploadsSaga from '../../redux/uploads/saga';
import deviationsSaga from '../../redux/deviations/saga';
import { initialize as initConfig } from '@wix/da-redux-config/pkg/actions';
import { getConfig } from '@wix/da-redux-config/pkg/selectors';
import { getPublicSession } from '../../publicSession/selectors';
import { initialize as initPublicSession } from '../../publicSession/actions';
import { getFlags } from '@wix/da-react-context/pkg/flags/redux/selectors';
import flagsReducers from '@wix/da-react-context/pkg/flags/redux/reducer';
import { setFlags } from '@wix/da-react-context/pkg/flags/redux/actions';
import mediaPopupReducers from '../reducers';

import mediaPopupSaga from '../saga';

interface UseSelector {
  <TState = unknown, Selected = unknown>(
    selector: (state: TState) => Selected,
    equalityFn?: EqualityFn<Selected>
  ): Selected;
  <TState = unknown, Selected = unknown>(
    selector: (state: TState) => Selected,
    options?: UseSelectorOptions<Selected>
  ): Selected;
}

const MediaContext = React.createContext<any>(null);
MediaContext.displayName = 'MediaContext';

export const useMMStore = createStoreHook(MediaContext);
export const useMMSelector: UseSelector = createSelectorHook(MediaContext);
export const useMMDispatch = createDispatchHook(MediaContext);

export function useMMDispatchActions<M extends ActionCreatorsMapObject<any>>(actions: M): M {
  const MMDispatch = useMMDispatch();
  const boundActionsRef = useRef(bindActionCreators(actions, MMDispatch));
  return boundActionsRef.current;
}

export const connectMediaStore: typeof connect = (
  mapStateToProps: any = null,
  mapDispatchToProps: any = null,
  mergeProps: any = null,
  options: any = {}
) => {
  options.context = MediaContext;
  return connect(mapStateToProps, mapDispatchToProps, mergeProps, options);
};

export const reducers = {
  ...sharedConfigReducers,
  ...entitiesReducers,
  ...streamReducers,
  ...publicSessionReducers,
  ...mediaPopupReducers,
  ...flagsReducers,
  ...modalsReducers,
  ...uploadsReducers,
};

export const sagas = [mediaPopupSaga, ...streamSagas, modalsSaga, uploadsSaga, deviationsSaga];

export function* rootSaga() {
  yield all(sagas.map(fork));
}

const withOwnReduxStore = (Component): any => {
  const enhance = connect(state => ({
    config: getConfig(state),
    publicSession: getPublicSession(state),
    flags: getFlags(state),
  }));
  class ComponentWithOwnReduxStore extends React.Component {
    store;
    constructor(props) {
      super(props);

      const {
        config,
        flags,
        publicSession,
        // although MM will have its own store and dispatch created below,
        // we add the external/app store's dispatch to our sagas so that
        // we can dispatch external app actions (like addNotification, etc.)
        dispatch: externalDispatch,
      } = props;
      const sagaMiddleware = createSagaMiddleware({
        context: { externalDispatch },
      });
      const composeEnhancers = composeWithDevTools({ name: 'MediaPopup' });
      this.store = createStore(
        combineReducers(reducers),
        composeEnhancers(applyMiddleware(sagaMiddleware))
      );
      this.store.dispatch(initConfig(config));
      this.store.dispatch(initPublicSession({ sessionData: publicSession }));
      this.store.dispatch(setFlags(flags));

      sagaMiddleware.run(rootSaga);
    }
    render() {
      return (
        <Provider context={MediaContext} store={this.store}>
          <Component {...this.props} />
        </Provider>
      );
    }
  }
  return enhance(ComponentWithOwnReduxStore);
};

export default withOwnReduxStore;
